// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { RemoteMarkdown } from '@/components/remote/RemoteMarkdown';
import { ToolInputOutputPanel } from '@/components/remote/chat/ToolInputOutputPanel';
import { DisclosureTrigger } from '@/components/remote/primitives/Disclosure';
import ShinyText from '@/components/ui/ShinyText';
import { MorphIcon } from '@/components/ui/morph-icon';
import type {
  AgentExecutionView,
  ReasoningSummaryItem,
  ToolCallItem,
  WorkItem,
} from '@web/lib/remoteWorkItemModel';
import {
  Code2,
  Database,
  FilePenLine,
  Files,
  Globe2,
  Image,
  Mail,
  Search,
  Terminal,
  Wrench,
} from 'lucide';
import type { IconInput } from 'morphicons/react';
import { useId } from 'react';

function isRunning(item: WorkItem): boolean {
  return item.status === 'queued' || item.status === 'in_progress';
}

function ownerName(
  agent: AgentExecutionView | undefined,
  rootAgentKeys: ReadonlySet<string>
): string | null {
  return agent && !rootAgentKeys.has(agent.key) ? agent.name : null;
}

function reasoningText(item: ReasoningSummaryItem): string {
  return (
    item.summaryText?.trim() ||
    item.parts
      .map((part) => part.text)
      .join('')
      .trim() ||
    'Thinking through the next step'
  );
}

function itemText(item: Exclude<WorkItem, ToolCallItem>): string {
  switch (item.type) {
    case 'reasoning_summary':
      return reasoningText(item);
    case 'agent_message':
      return item.text?.trim() || 'Agent update';
    case 'human_input':
      return item.response === undefined
        ? item.question || 'Agent question'
        : `Answered: ${item.question || 'Agent question'}`;
    case 'plan':
    case 'notice':
      return item.text?.trim() || (item.type === 'plan' ? 'Plan' : 'Update');
    case 'agent_call':
      return item.prompt?.trim() || 'Delegated agent work';
    case 'unknown':
      return `Unsupported step: ${item.originalType || 'unknown'}`;
  }
}

function markdownText(
  item: Exclude<WorkItem, ToolCallItem>,
  agent: AgentExecutionView | undefined,
  rootAgentKeys: ReadonlySet<string>
): string {
  const owner = ownerName(agent, rootAgentKeys);
  const text = itemText(item);
  return owner ? `**${owner}:** ${text}` : text;
}

export type ToolActionIconKind =
  | 'code'
  | 'database'
  | 'edit'
  | 'fallback'
  | 'image'
  | 'mail'
  | 'read'
  | 'search'
  | 'terminal'
  | 'web';

export type ToolActionPresentation = {
  action: string;
  icon: IconInput;
  iconKind: ToolActionIconKind;
};

function humanizeToolName(toolName: string): string {
  const cleaned = toolName
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
  if (!cleaned) return 'Used tool';
  return `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1)}`;
}

export function resolveToolActionPresentation(
  toolName: string
): ToolActionPresentation {
  const normalized = toolName.trim().toLowerCase().replace(/[_-]+/g, ' ');

  if (/image|screenshot|photo/.test(normalized)) {
    return {
      action: /view|open|inspect|screenshot/.test(normalized)
        ? 'Viewed images'
        : humanizeToolName(toolName),
      icon: Image,
      iconKind: 'image',
    };
  }
  if (/database|postgres|sql|warehouse/.test(normalized)) {
    return {
      action: humanizeToolName(toolName),
      icon: Database,
      iconKind: 'database',
    };
  }
  if (
    /apply patch|edit|patch|write file|create file|update file/.test(normalized)
  ) {
    return { action: 'Edited files', icon: FilePenLine, iconKind: 'edit' };
  }
  if (/shell|terminal|command|exec|bash|powershell/.test(normalized)) {
    return { action: 'Ran commands', icon: Terminal, iconKind: 'terminal' };
  }
  if (/mail|email|inbox/.test(normalized)) {
    return {
      action: humanizeToolName(toolName),
      icon: Mail,
      iconKind: 'mail',
    };
  }
  if (/search|find|query|research/.test(normalized)) {
    return {
      action: humanizeToolName(toolName),
      icon: Search,
      iconKind: 'search',
    };
  }
  if (/read file|open file|reader|document|pdf/.test(normalized)) {
    return { action: 'Read files', icon: Files, iconKind: 'read' };
  }
  if (/browser|web|url|http|fetch/.test(normalized)) {
    return {
      action: humanizeToolName(toolName),
      icon: Globe2,
      iconKind: 'web',
    };
  }
  if (/code|test|build|compile/.test(normalized)) {
    return {
      action: humanizeToolName(toolName),
      icon: Code2,
      iconKind: 'code',
    };
  }
  return {
    action: humanizeToolName(toolName),
    icon: Wrench,
    iconKind: 'fallback',
  };
}

export function AgentMarkdownStep({
  item,
  agent,
  rootAgentKeys,
}: {
  item: Exclude<WorkItem, ToolCallItem>;
  agent?: AgentExecutionView;
  rootAgentKeys: ReadonlySet<string>;
}) {
  return (
    <div
      className="min-w-0 px-1 py-2"
      data-work-item-id={item.id}
      data-work-item-type={item.type}
    >
      <RemoteMarkdown
        content={markdownText(item, agent, rootAgentKeys)}
        className="text-ds-text-neutral-default-default"
      />
      {item.type === 'reasoning_summary' && item.textTruncated && (
        <p className="m-0 mt-1 text-label-xs text-ds-text-neutral-muted-default">
          Reasoning summary was truncated.
        </p>
      )}
    </div>
  );
}

export function AgentToolAction({
  items,
  agent,
  showAgentName,
  expanded,
  onExpandedChange,
}: {
  items: ToolCallItem[];
  agent?: AgentExecutionView;
  showAgentName: boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const first = items[0];
  const detailId = `flat-tool-${useId().replace(/:/g, '')}`;
  if (!first) return null;

  const presentation = resolveToolActionPresentation(first.toolName || '');
  const owner = showAgentName ? agent?.name || 'Agent' : null;
  const count = items.length > 1 ? ` · ${items.length}` : '';
  const label = `${owner ? `${owner} · ` : ''}${presentation.action}${count}`;
  const running = items.some(isRunning);

  return (
    <div
      className="min-w-0"
      data-work-action-id={first.key}
      data-tool-icon={presentation.iconKind}
    >
      <DisclosureTrigger
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        controlsId={detailId}
        expandedChevron="down"
        chevronPlacement="inline"
        ariaLabel={`${expanded ? 'Collapse' : 'Expand'} ${label} action details`}
        className="min-h-9 gap-1.5 rounded-lg px-1 py-1.5 hover:bg-ds-bg-neutral-subtle-hover"
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <MorphIcon
            icon={presentation.icon}
            aria-hidden
            className="h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
          />
          {running ? (
            <ShinyText
              text={label}
              speed={2.4}
              className="block min-w-0 truncate text-body-sm"
            />
          ) : (
            <span className="block min-w-0 truncate text-body-sm text-ds-text-neutral-muted-default">
              {label}
            </span>
          )}
        </span>
      </DisclosureTrigger>

      {expanded && (
        <div
          id={detailId}
          role="region"
          aria-label={`${label} input and output`}
          className="ml-2 border-x-0 border-b-0 border-l border-r-0 border-t-0 border-solid border-ds-border-neutral-subtle-default py-2 pl-5 pr-1"
        >
          <div className="flex min-w-0 flex-col gap-4">
            {items.map((item, index) => (
              <div key={item.key} data-work-item-id={item.id}>
                {items.length > 1 && (
                  <p className="m-0 mb-2 text-label-xs font-medium text-ds-text-neutral-muted-default">
                    Call {index + 1}
                  </p>
                )}
                <ToolInputOutputPanel
                  input={item.input}
                  output={item.output}
                  result={item.result}
                  error={item.error}
                  exitCode={item.exitCode}
                  cwd={item.cwd}
                  outputTruncated={item.outputTruncated}
                  outputByteCount={item.outputByteCount}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
